/**
 * Chip Denomination Colors for Poker Reborn
 *
 * 15 denominations following the 1-5-25-100-500 pattern across 3 tiers.
 * Textures loaded from sprite atlas (step_4_1 pipeline output).
 *
 * @see docs/screens/room/CHIPS.md - Full chip specification
 * @see docs/screens/room/CHIP_DENOMINATIONS.md - Denomination system
 */

import { Assets, Texture, Sprite, Spritesheet } from 'pixi.js';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// TYPES
// =============================================================================

export interface ChipDenomination {
  /** Chip value */
  value: number;
  /** Render name prefix in atlas (e.g. 'chip_001', 'chip_1K') */
  renderName: string;
  /** Main chip color */
  color: number;
  /** Accent color (highlights) */
  accent: number;
  /** Border/edge color */
  border: number;
  /** Human-readable color name */
  name: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Atlas path — loaded as PixiJS spritesheet */
const CHIPS_ATLAS_PATH = 'assets/atlases/chips.json';

/** Rotation angles available in atlas (Z-axis flat spin) */
const ROTATION_ANGLES = ['000', '030', '060', '300', '330'] as const;

/** Native sprite size in atlas (pixels) */
export const CHIP_SPRITE_SIZE = 256;

/** Base scale for flying chip animations (targets 36px display at 1920px = small chip size) */
export const CHIP_FLYING_SCALE = 36 / CHIP_SPRITE_SIZE;

/** BBS breakpoints for flying chip display size (smooth interpolation) */
const FLYING_CHIP_SIZE_BREAKPOINTS: Record<number, number> = { 375: 24, 720: 30, 1440: 36, 1920: 36 };

/**
 * Get chip flying scale for a given screen width (BBS-responsive).
 * Use this instead of CHIP_FLYING_SCALE when screen width is available.
 */
export function getChipFlyingScale(screenWidth: number): number {
  const targetSize = getScaledValue(screenWidth, FLYING_CHIP_SIZE_BREAKPOINTS, { discrete: false });
  return targetSize / CHIP_SPRITE_SIZE;
}

// =============================================================================
// DENOMINATION TABLE
// =============================================================================

/**
 * Standard poker chip denominations with colors.
 * 1-5-25-100-500 pattern × 3 tiers = 15 denominations.
 * Ordered from lowest to highest value.
 *
 * @see docs/screens/room/CHIP_DENOMINATIONS.md
 */
export const CHIP_DENOMINATIONS: readonly ChipDenomination[] = [
  // Tier 1: Micro/Low
  { value: 1,           renderName: 'chip_001',  color: 0xffffff, accent: 0xe8e8e8, border: 0xcccccc, name: 'white' },
  { value: 5,           renderName: 'chip_005',  color: 0xcc0000, accent: 0xff3333, border: 0x990000, name: 'red' },
  { value: 25,          renderName: 'chip_025',  color: 0x00aa00, accent: 0x33cc33, border: 0x006600, name: 'green' },
  { value: 100,         renderName: 'chip_100',  color: 0x1a1a1a, accent: 0x333333, border: 0x000000, name: 'black' },
  { value: 500,         renderName: 'chip_500',  color: 0x9900cc, accent: 0xcc33ff, border: 0x660099, name: 'purple' },
  // Tier 2: Mid/High
  { value: 1_000,       renderName: 'chip_1K',   color: 0xffb800, accent: 0xffcc33, border: 0xcc9900, name: 'yellow' },
  { value: 5_000,       renderName: 'chip_5K',   color: 0xff66b2, accent: 0xff99cc, border: 0xcc3366, name: 'pink' },
  { value: 25_000,      renderName: 'chip_25K',  color: 0x003366, accent: 0x0055aa, border: 0x002244, name: 'navy' },
  { value: 100_000,     renderName: 'chip_100K', color: 0x800020, accent: 0xaa3344, border: 0x660018, name: 'burgundy' },
  { value: 500_000,     renderName: 'chip_500K', color: 0x008080, accent: 0x33aaaa, border: 0x006666, name: 'teal' },
  // Tier 3: Elite/Legendary
  { value: 1_000_000,   renderName: 'chip_1M',   color: 0xd4af37, accent: 0xf0d060, border: 0xa08020, name: 'gold' },
  { value: 5_000_000,   renderName: 'chip_5M',   color: 0xc0c0c0, accent: 0xe0e0e0, border: 0x909090, name: 'silver' },
  { value: 25_000_000,  renderName: 'chip_25M',  color: 0xe5e4e2, accent: 0xf5f5f5, border: 0xb0b0b0, name: 'platinum' },
  { value: 100_000_000, renderName: 'chip_100M', color: 0x88ccff, accent: 0xaaddff, border: 0x5599cc, name: 'crystal' },
  { value: 500_000_000, renderName: 'chip_500M', color: 0xe0115f, accent: 0xff3377, border: 0xaa0040, name: 'ruby' },
];

/**
 * Denominations sorted highest to lowest for breakdown calculation.
 */
export const DENOMINATIONS_DESC = [...CHIP_DENOMINATIONS].reverse();

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get chip denomination info by value.
 */
export function getChipDenomination(value: number): ChipDenomination | undefined {
  return CHIP_DENOMINATIONS.find((d) => d.value === value);
}

/**
 * Get the color for a chip denomination value.
 */
export function getChipColor(denominationValue: number): number {
  const denom = getChipDenomination(denominationValue);
  return denom?.color ?? 0x888888;
}

/**
 * Calculate chip breakdown for a given total value.
 * Uses greedy algorithm to minimize chip count.
 *
 * @param totalValue - The total value to break down
 * @returns Map of denomination value to count
 *
 * @example
 * calculateChipBreakdown(555) // { 500: 1, 25: 2, 5: 1 }
 */
export function calculateChipBreakdown(
  totalValue: number
): Map<number, number> {
  const breakdown = new Map<number, number>();
  let remaining = Math.floor(totalValue);

  for (const denom of DENOMINATIONS_DESC) {
    if (remaining >= denom.value) {
      const count = Math.floor(remaining / denom.value);
      breakdown.set(denom.value, count);
      remaining = remaining % denom.value;
    }
  }

  return breakdown;
}

/**
 * Calculate a realistic chip breakdown that mimics a real poker table.
 *
 * Uses the "Two-Denomination Fill" algorithm: picks the lowest denomination
 * that fits within the visual capacity, then mixes with one denomination
 * higher when overflow would occur. This produces 2-3 chip colors with
 * tall same-color stacks — like a real poker pot.
 *
 * @param totalValue - The total chip value to break down
 * @param capacity - Maximum number of chips that can be displayed
 * @returns Map of denomination value to count (ordered high to low)
 *
 * @see docs/screens/room/CHIP_BREAKDOWN.md - Full algorithm specification
 *
 * @example
 * calculateRealisticBreakdown(500, 36)   // { 25: 20 }              — 20 chips
 * calculateRealisticBreakdown(5050, 36)  // { 500: 4, 100: 30, 25: 2 } — 36 chips
 * calculateRealisticBreakdown(100, 12)   // { 25: 2, 5: 10 }        — 12 chips
 */
export function calculateRealisticBreakdown(
  totalValue: number,
  capacity: number,
): Map<number, number> {
  const breakdown = new Map<number, number>();
  let remaining = Math.floor(totalValue);

  if (remaining <= 0 || capacity <= 0) return breakdown;

  // Try greedy first — if it produces fewer than 4 denomination types
  // and fits within capacity, use it (clean, one pile per denomination).
  const greedyBreakdown = calculateChipBreakdown(totalValue);
  if (greedyBreakdown.size < 4) {
    let greedyTotal = 0;
    for (const count of greedyBreakdown.values()) {
      greedyTotal += count;
    }
    if (greedyTotal <= capacity) {
      return greedyBreakdown;
    }
  }

  // Step 1: Find the natural denomination (highest that fits the value at least once).
  // Never go below this — $5 stays as $5, not 5×$1.
  let naturalDenomIdx = 0;
  for (let i = CHIP_DENOMINATIONS.length - 1; i >= 0; i--) {
    const denom = CHIP_DENOMINATIONS[i];
    if (denom && denom.value <= remaining) {
      naturalDenomIdx = i;
      break;
    }
  }

  // Find the base denomination (lowest where count ≤ capacity, but never below natural)
  let baseDenomIdx = -1;

  for (let i = naturalDenomIdx; i < CHIP_DENOMINATIONS.length; i++) {
    const denom = CHIP_DENOMINATIONS[i];
    if (!denom) continue;
    const count = Math.floor(remaining / denom.value);
    if (count <= capacity) {
      baseDenomIdx = i;
      break;
    }
  }

  // Edge case: even the highest denomination overflows — greedy fallback
  if (baseDenomIdx === -1) {
    return calculateChipBreakdown(totalValue);
  }

  const baseDenom = CHIP_DENOMINATIONS[baseDenomIdx];
  if (!baseDenom) return calculateChipBreakdown(totalValue);

  // Step 2: Check if the denomination below baseDenom would overflow
  const belowDenomIdx = baseDenomIdx - 1;
  const belowDenom = belowDenomIdx >= 0 ? CHIP_DENOMINATIONS[belowDenomIdx] : undefined;

  if (!belowDenom || Math.floor(remaining / belowDenom.value) <= capacity) {
    // No denomination below, or it also fits — use single denomination
    const count = Math.floor(remaining / baseDenom.value);
    if (count > 0) {
      breakdown.set(baseDenom.value, count);
      remaining -= count * baseDenom.value;
    }
  } else {
    // belowDenom overflows — mix baseDenom (high) and belowDenom (low)
    // Find minimum baseDenom count so that base + below ≤ capacity
    const maxBaseCount = Math.floor(remaining / baseDenom.value);

    let baseCount = -1;
    let belowCount = 0;

    for (let hc = 0; hc <= maxBaseCount; hc++) {
      const afterBase = remaining - hc * baseDenom.value;
      const bc = Math.floor(afterBase / belowDenom.value);
      if (hc + bc <= capacity) {
        baseCount = hc;
        belowCount = bc;
        break;
      }
    }

    // If mixing loop found no valid solution, fall back to single baseDenom
    if (baseCount === -1) {
      const count = Math.floor(remaining / baseDenom.value);
      if (count > 0) {
        breakdown.set(baseDenom.value, count);
        remaining -= count * baseDenom.value;
      }
    } else {
      if (baseCount > 0) {
        breakdown.set(baseDenom.value, baseCount);
        remaining -= baseCount * baseDenom.value;
      }
      if (belowCount > 0) {
        breakdown.set(belowDenom.value, belowCount);
        remaining -= belowCount * belowDenom.value;
      }
    }
  }

  // Step 3: Handle remainder with greedy on lower denominations (capacity-aware)
  if (remaining > 0) {
    let usedChips = 0;
    for (const count of breakdown.values()) {
      usedChips += count;
    }
    const remainingCapacity = capacity - usedChips;

    const startIdx = belowDenom ? belowDenomIdx - 1 : baseDenomIdx - 1;
    let addedInRemainder = 0;
    for (let i = startIdx; i >= 0; i--) {
      const denom = CHIP_DENOMINATIONS[i];
      if (!denom) continue;
      if (remaining >= denom.value) {
        const maxCount = remainingCapacity - addedInRemainder;
        const count = Math.min(Math.floor(remaining / denom.value), maxCount);
        if (count > 0) {
          breakdown.set(denom.value, count);
          remaining -= count * denom.value;
          addedInRemainder += count;
        }
      }
      if (remaining === 0 || addedInRemainder >= remainingCapacity) break;
    }
  }

  return breakdown;
}

/**
 * Get total chip count from a breakdown.
 */
export function getChipCount(breakdown: Map<number, number>): number {
  let count = 0;
  for (const chipCount of breakdown.values()) {
    count += chipCount;
  }
  return count;
}

/**
 * Determine visual chip count based on bet value.
 * Larger bets show more chips (capped at 5).
 */
export function getVisualChipCount(value: number): number {
  if (value < 10) return 1;
  if (value < 100) return 2;
  if (value < 1000) return 3;
  if (value < 10000) return 4;
  return 5;
}

// =============================================================================
// TEXTURE LOADING (Atlas-based)
// =============================================================================

/** Track loaded chip textures */
let chipTexturesLoaded = false;

/**
 * Preload chip sprite atlas.
 * Call this during app initialization.
 */
export async function preloadChipTextures(): Promise<void> {
  if (chipTexturesLoaded) return;

  await Assets.load(CHIPS_ATLAS_PATH);
  chipTexturesLoaded = true;
}

/**
 * Get a chip texture for a denomination.
 * Returns a sprite frame from the atlas at a random rotation angle
 * (for visual variety in stacks).
 *
 * @param denominationValue - The chip value
 * @param angleIndex - Which rotation angle (0-4), random if omitted
 * @returns The texture or undefined if not found
 */
export function getChipTexture(
  denominationValue: number,
  angleIndex?: number,
  withShadow = false
): Texture | undefined {
  const denom = getChipDenomination(denominationValue);
  if (!denom) return undefined;

  const spritesheet = Assets.get<Spritesheet>(CHIPS_ATLAS_PATH);
  if (!spritesheet) return undefined;

  const selectedAngleIndex = angleIndex ?? Math.floor(Math.random() * ROTATION_ANGLES.length);
  const angle = ROTATION_ANGLES[selectedAngleIndex % ROTATION_ANGLES.length];
  const suffix = withShadow ? '_s' : '';
  const frameName = `${denom.renderName}_top_${angle}${suffix}`;

  return spritesheet.textures[frameName];
}

/**
 * Create a chip sprite for a denomination.
 * @param denominationValue - The chip value
 * @param angleIndex - Which rotation angle (0-4) - random for variety
 * @returns A new Sprite or undefined if texture not found
 */
export function createChipSprite(
  denominationValue: number,
  angleIndex?: number,
  withShadow = false
): Sprite | undefined {
  const texture = getChipTexture(denominationValue, angleIndex, withShadow);
  if (!texture) return undefined;

  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5, 0.5);
  return sprite;
}

/**
 * Get the best denomination for a given amount.
 * Returns the highest denomination that doesn't exceed the amount.
 */
export function getBestDenomination(amount: number): ChipDenomination {
  for (const denom of DENOMINATIONS_DESC) {
    if (denom.value <= amount) {
      return denom;
    }
  }
  return CHIP_DENOMINATIONS[0] as ChipDenomination;
}

/**
 * Get chip denominations for visual display of an amount.
 * Returns array of denomination values for the chips to show.
 */
export function getChipDenominationsForAmount(
  amount: number,
  maxChips: number = 5
): number[] {
  const denominations: number[] = [];
  let remaining = amount;

  for (const denom of DENOMINATIONS_DESC) {
    while (remaining >= denom.value && denominations.length < maxChips) {
      denominations.push(denom.value);
      remaining -= denom.value;
    }
  }

  // If we couldn't fit any chips, add one of the smallest denomination
  if (denominations.length === 0 && amount > 0) {
    denominations.push(CHIP_DENOMINATIONS[0]?.value ?? 1);
  }

  return denominations;
}
